import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import { toSnakeCase } from './stringFormatting';
import { panic } from 'src/utils/mixpanel';

const colorMap = {
  darkBlue: '#031425',
  darkBlueBlue20: '#03142533',
  lightOpaqueBlue: '#DCE3F6',
  opaqueGrey: '#E5E9EE',
  graysSecondaryText: '#546379',
  graysPrimaryOpaque: 'rgba(246, 247, 248, 0.5)',
  greenTagOpaque: 'rgba(113, 193, 132, 0.2)',
  blueTagOpaque: 'rgba(78, 116, 208, 0.2)',
  stacklineBlue: '#46a8f6',
  comparison: '#adbdcc',
  lightBlue: '#a5cfff',
  lightestBlue: '#f6faff',
  red: '#f35379',
  green: '#39b3ae',
  greenButton: '#56b788',
  lightGrey: '#cfd7df',
  blueGrey: '#bec7d3',
  grey: '#7e8fa8',
  graysPrimary: '#f6f7f8',
  graysSecondary: '#E8E8ED',
  greyLightBorder: '#bfbfbf',
  darkerGrey: '#96a5b3',
  veryLightGrey: '#f5f5f5',
  orangeLightGrey: '#f9f9f9',
  darkerAlertGrey: '#cccccc',
  actualSpend: '#031425',
  projectedSpend: '#46a8f6',
  greyHover: '#F2F4F6',
  surplusBudgetOpaque: '#ECEFF2',
  surplusBudget: '#cfd7df',
  overpacing: '#fec349',
  titleScore: '#46a8f6',
  bulletsScore: '#56b788',
  imagesScore: '#635bff',
  videosScore: '#ff0083',
  blue: '#2a8ade',
  brightBlue: '#69d5f8',
  pastelGreen: '#e3f3e6',
  blueGreen: '#3297d3',
  filterBlue: '#337ab7',
  deepGreen: '#35aaa1',
  lightGreen: '#7ce4df',
  lighterGrey: '#e1e4e9',
  lightestGrey: '#f6f9fc',
  lightestGrey2: '#E6E9ED',
  borderGrey: '#dedede',
  darkGrey: '#9fabbd',
  darkGreen: '#185450',
  labelGrey: '#7e8fa8cc',
  white: '#f6f9fc',
  purple: '#6772e5',
  lightPurple: '#7b85e9',
  lightestPurple: '#adb3f1',
  beaconPurple: '#665fa3',
  pink: '#d782d9',
  lightPink: '#ffc6ef',
  orange: '#ff965b',
  lightOrange: '#fcd669',
  quartz: '#d7d9e9',
  atlasGreen: '#5db3a1',
  atlasDarkGreen: '#239692',
  atlasBlue: '#195aa2',
  magenta: '#A21671',
  atlasDarkGrey: '#96aed4',
  atlasGrey: '#b0c7ed',
  atlasTeal: '#3ab2ae',
  advertisingBlue: '#3c4859',
  redBright: '#f44a36',
  gold: '#ffbd32',
  teal: '#16949B',
  errorRed: '#ff534A',
  userManagementErrorPink: '#fcdde4',
  userManagementSuccessGreen: '#ddefe6',
  userManagementBulkErrorPink: '#f05479',
  searchGrey: '#8293A4',
  searchGreyLight: '#8C8C8C',
  contentGenBackground: '#FBFBFB',
  brandClubGreen: '#71C184',
  brandClubYellow: '#E6A00C',
  skeletonGrey: '#f0f0f0',
  dividerGrey: '#CDD4DB'
};

/**
 * Using the colors from the `src/styles/_colors.scss` file, creates a JavaScript object with their color values
 * after transforming color names containing dashes to snakeCase.  This allows the same color names as used in the
 * CSS to be used in JS.
 *
 * The `eslint` rule disable is due to custom behavior that only takes place when we're in dev mode.  See the
 * `installDevTypecheckProxy` for more info.
 */
let mappedColors = {
  // Placeholders to serve as documentation for available colors + facilitate editor autocomplete
  actualSpend: '',
  projectedSpend: '',
  surplusBudget: '',
  opaqueGrey: '',
  graysSecondaryText: '',
  surplusBudgetOpaque: '',
  overpacing: '',
  titleScore: '',
  magenta: '',
  bulletsScore: '',
  imagesScore: '',
  teal: '',
  gold: '',
  videosScore: '',
  pastelGreen: '',
  darkBlue: '',
  lightOpaqueBlue: '',
  graysPrimaryOpaque: '',
  greenTagOpaque: '',
  blueTagOpaque: '',
  errorRed: '',
  darkBlueBlue20: '',
  blue: '',
  stacklineBlue: '',
  graysPrimary: '',
  lightBlue: '',
  lightestBlue: '',
  greenButton: '',
  greyHover: '',
  darkGreen: '',
  comparison: '',
  brightBlue: '',
  blueGreen: '',
  labelGrey: '',
  filterBlue: '',
  green: '',
  lightGreen: '',
  borderGrey: '',
  blueGrey: '',
  grey: '',
  lightGrey: '',
  lighterGrey: '',
  lightestGrey: '',
  lightestGrey2: '',
  darkerGrey: '',
  darkGrey: '',
  black: '',
  white: '',
  purple: '',
  lightPurple: '',
  lightestPurple: '',
  beaconPurple: '',
  pink: '',
  lightPink: '',
  orange: '',
  lightOrange: '',
  red: '',
  quartz: '',
  default: '',
  atlasGreen: '',
  atlasDarkGreen: '',
  atlasBlue: '',
  atlasDarkGrey: '',
  atlasGrey: '',
  atlasTeal: '',
  advertisingBlue: '',
  redBright: '',
  userManagementBulkErrorPink: '',
  graysSecondary: '',
  searchGrey: '',
  searchGreyLight: '',
  contentGenBackground: '',
  brandClubGreen: '',
  brandClubYellow: '',
  veryLightGrey: '',
  orangeLightGrey: '',
  darkerAlertGrey: '',
  skeletonGrey: '',
  dividerGrey: ''
};

/**
 * Creates some manual type checking for the `colors` object if we're in dev mode.  The `mappedColors` object
 * is replaced with a wrapped version that uses a `Proxy` to intercept key accesses and checks to make sure the
 * requested color key actually exists.
 */
const installDevTypecheckProxy = () => {
  const handlers = {
    get: (obj, key) => {
      const color = obj[key];
      if (_isEmpty(color)) {
        return panic(`Attempted to get color with key "${key}" but no color exists with that name.`);
      }
      return color;
    },
    set: (obj, key, value) => {
      return panic(`Attempted to mutate the colors object; Tried to set key "${key}" to ${value}`);
    }
  };

  const proxy = new Proxy(_cloneDeep(mappedColors), handlers);
  // Replace `mappedColors` with the proxy, intercepting all accesses and attempted mutations.
  mappedColors = proxy;
};

// Update `mappedColors` with values from the `_colors.scss` file
Object.entries(colorMap).forEach(([key, value]) => {
  mappedColors[toSnakeCase(key)] = value;
});

if (__DEV__) {
  installDevTypecheckProxy();
}

export default mappedColors;
